import { EntityType } from '@frontend/common';
import { Application, Feature } from '@frontend/functionality/types';
import { PackageClient } from '@frontend/package/api';
import { PackageStateName } from '@frontend/package/types';
import { SettingClient } from '@frontend/setting/api';
import { TransactionPudoExtendedClient } from '@frontend/transaction/api';
import { ExtendedPudoTransaction, TransactionStateName } from '@frontend/transaction/types';
import { updateFunctionalityContext } from '@frontend/wizard/mobile';
import { ScreenTrigger, ScreenType, Step } from '@frontend/wizard/types';
import { useEffect, useState } from 'react';

import { useLicenseRepository } from '../../../utils/license-repository';
import { useWizardPackage } from '../../hooks/useWizardPackage';
import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { useScreen } from '../useScreen';

interface ViewProps {
    userTransactions: ExtendedPudoTransaction[] | null;
    pinStep?: Step;
    scanStep?: Step;
    falseStep?: Step;
    onOpenDropoff: () => void;
    onContinue: (nextStep?: Step | undefined) => void;
    onTransactionContinue: (transaction: ExtendedPudoTransaction) => void;
    transactionDataLoading: ExtendedPudoTransaction | null;
    onBack: (type: ScreenType) => void;
    isNivaliz: boolean;
}

const useAuthenticatedDropOffMethod = (): ViewProps => {
    const { store, onBack, onSubmit: onContinue, navigationState, dispatch } = useScreen();
    const { functionalities } = useLicenseRepository();
    const transactionContext = useWizardTransaction({
        wizardId: store!.wizard.id,
        store,
        autoCreate: false,
        autoFetch: false
    });
    const pkgContext = useWizardPackage({
        wizardId: store!.wizard.id,
        store,
        autoCreate: false,
        autoFetch: false
    });

    const [openDropoff, setOpenDropoff] = useState<Step | undefined>(undefined);
    const [userTransactions, setUserTransactions] = useState<ExtendedPudoTransaction[] | null>(null);
    const [transactionDataLoading, setTransactionDataLoading] = useState<ExtendedPudoTransaction | null>(null);

    useEffect(() => {
        if (navigationState.iot && navigationState.user) {
            TransactionPudoExtendedClient.fetchPudoExtendedTransactions({
                sender_id: navigationState.user.id,
                state: TransactionStateName.TRANSACTION_DROP_OFF_PENDING,
                spot_id: navigationState.iot.spot_id
            })
                .then((res) => {
                    setUserTransactions(res.results);
                })
                .catch(() => {
                    setUserTransactions([]);
                });
        }
    }, [navigationState]);

    useEffect(() => {
        if (store?.steps && functionalities.find((func) => func.feature === Feature.OPEN_DROP_OFF))
            setOpenDropoff(
                store?.steps.find((step) => step.screen === ScreenType.AUTHENTICATED_DROP_OFF_METHOD && step.trigger === ScreenTrigger.SUBMIT_FALSE)
            );
    }, [store?.steps]);

    useEffect(() => {
        transactionContext.localUpdateTransaction({ feature: store?.functionality.feature });
    }, []);

    const onOpenDropoff = async () => {
        const step = store?.steps.find((step) => step.screen === ScreenType.AUTHENTICATED_DROP_OFF_METHOD && step.trigger === ScreenTrigger.SUBMIT_FALSE);
        const openDropoffFunctionality = functionalities.find((func) => func.feature === Feature.OPEN_DROP_OFF);
        if (!step || !store || !openDropoffFunctionality) return;

        const settingsRes = await SettingClient.fetchSettings({
            entity_type: EntityType.FUNCTIONALITY,
            entity_id: openDropoffFunctionality.id
        });
        dispatch(
            updateFunctionalityContext({
                key: store.wizard.id,
                functionality: openDropoffFunctionality,
                functionalitySettings: settingsRes.results
            })
        );

        await transactionContext.localUpdateTransaction({ feature: Feature.OPEN_DROP_OFF });
        onContinue(step);
    };

    const onTransactionContinue = async (transaction: ExtendedPudoTransaction) => {
        setTransactionDataLoading(transaction);
        const step = store?.steps.find(
            (step) =>
                step.screen === ScreenType.AUTHENTICATED_DROP_OFF_METHOD &&
                step.trigger === ScreenTrigger.SUBMIT &&
                step.next_screen !== ScreenType.DROP_OFF_PIN_CODE &&
                step.next_screen !== ScreenType.DROP_OFF_SCAN_QR_CODE
        );
        try {
            const pkgs = await PackageClient.fetchPackages({
                transaction_id: transaction.id,
                type: 'drop_off',
                state: PackageStateName.PACKAGE_DROP_OFF_PENDING
            });
            if (pkgs.results.length > 0) {
                await transactionContext.localUpdateTransaction(transaction);
                await pkgContext.localUpdatePackage(pkgs.results[0]);
                onContinue(step);
            }
        } finally {
            setTransactionDataLoading(null);
        }
    };

    return {
        userTransactions,
        pinStep: store?.steps.find(
            (step) =>
                step.screen === ScreenType.AUTHENTICATED_DROP_OFF_METHOD &&
                step.trigger === ScreenTrigger.SUBMIT &&
                step.next_screen === ScreenType.DROP_OFF_PIN_CODE
        ),
        scanStep: store?.steps.find(
            (step) =>
                step.screen === ScreenType.AUTHENTICATED_DROP_OFF_METHOD &&
                step.trigger === ScreenTrigger.SUBMIT &&
                step.next_screen === ScreenType.DROP_OFF_SCAN_QR_CODE
        ),
        falseStep: openDropoff,
        onOpenDropoff,
        onContinue,
        onTransactionContinue,
        transactionDataLoading,
        onBack,
        isNivaliz: store?.functionality.application === Application.NIVALIZ
    };
};

export default useAuthenticatedDropOffMethod;
